// The Library's "Add files" row, its drop state and the upload rows (progress, problems, announcements). Each
// supported app locale has its own copy so the Library never falls back to English.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  addFiles: 'Add files',
  addFilesLine:
    'Drop them here or browse. PDF, Word, Excel, PowerPoint, CSV and TSV, up to 500 MB each.',
  addFilesLineShort: 'PDF, Office, CSV/TSV · up to 500 MB',
  browse: 'Browse',
  dropTitle: 'Release to add {files}',
  dropFiles: 'one:file|other:files',
  dropLine: 'They’ll be private to you and ready to use in any chat.',
  waiting: 'Waiting to upload',
  progress: '{loaded} of {total}',
  timeLeft: 'About {time} left',
  finishing: 'Reading the file…',
  uploadingName: 'Uploading {name}',
  cancelName: 'Cancel upload of {name}',
  uploadedName: '{name} uploaded',
  paused: 'Upload paused, connection lost.',
  pausedName: '{name}: upload paused, connection lost.',
  retry: 'Retry',
  retryName: 'Retry {name}',
  remove: 'Remove',
  removeName: 'Remove {name}',
  unsupported: 'Can’t upload .{extension} files. Use PDF, Word, Excel, PowerPoint, CSV or TSV.',
  unsupportedUnnamed: 'Can’t upload this file. Use PDF, Word, Excel, PowerPoint, CSV or TSV.',
  unsupportedOld: 'Can’t upload .{extension} files. Save it as .{modern} first.',
  tooLarge: 'This file is {size}. The limit is 500 MB.',
  empty: 'This file is empty.',
  notInFilter: 'Not in this filter',
  useInChat: 'Use in chat',
  private: 'Private',
  storedOnly: 'Stored only · Chat cannot read this file yet',
  storedOnlyTooLarge: 'Stored only · Chat reads files up to 50 MiB',
} as const;

type Copy = typeof en;
export const libraryUploadCopy: NamespaceCatalog<keyof Copy> = {
  bg: {
    addFiles: 'Добавяне на файлове',
    addFilesLine:
      'Пуснете ги тук или ги изберете. PDF, Word, Excel, PowerPoint, CSV и TSV, до 500 MB всеки.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · до 500 MB',
    browse: 'Преглед',
    dropTitle: 'Пуснете, за да добавите {files}',
    dropFiles: 'one:файл|other:файла',
    dropLine: 'Ще бъдат лични за вас и готови за използване във всеки чат.',
    waiting: 'Изчаква качване',
    progress: '{loaded} от {total}',
    timeLeft: 'Още около {time}',
    finishing: 'Файлът се чете…',
    uploadingName: 'Качване на {name}',
    cancelName: 'Отказ от качването на {name}',
    uploadedName: 'Качването на {name} завърши',
    paused: 'Качването е на пауза, връзката е прекъсната.',
    pausedName: '{name}: качването е на пауза, връзката е прекъсната.',
    retry: 'Опитайте отново',
    retryName: 'Нов опит за качване на {name}',
    remove: 'Премахване',
    removeName: 'Премахване на {name}',
    unsupported:
      'Файлове .{extension} не могат да се качват. Използвайте PDF, Word, Excel, PowerPoint, CSV или TSV.',
    unsupportedUnnamed:
      'Този файл не може да се качи. Използвайте PDF, Word, Excel, PowerPoint, CSV или TSV.',
    unsupportedOld: 'Файлове .{extension} не могат да се качват. Първо го запазете като .{modern}.',
    tooLarge: 'Този файл е {size}. Ограничението е 500 MB.',
    empty: 'Този файл е празен.',
    notInFilter: 'Не е в този филтър',
    useInChat: 'Използване в чата',
    private: 'Личен',
    storedOnly: 'Само съхранен · Чатът още не може да прочете този файл',
    storedOnlyTooLarge: 'Само съхранен · Чатът чете файлове до 50 MiB',
  },
  hr: {
    addFiles: 'Dodaj datoteke',
    addFilesLine:
      'Ispustite ih ovdje ili ih odaberite. PDF, Word, Excel, PowerPoint, CSV i TSV, do 500 MB po datoteci.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · do 500 MB',
    browse: 'Pregledaj',
    dropTitle: 'Otpustite da biste dodali {files}',
    dropFiles: 'one:datoteku|few:datoteke|other:datoteka',
    dropLine: 'Bit će privatne za vas i spremne za upotrebu u svakom chatu.',
    waiting: 'Čeka prijenos',
    progress: '{loaded} od {total}',
    timeLeft: 'Još otprilike {time}',
    finishing: 'Čitanje datoteke…',
    uploadingName: 'Prijenos datoteke {name}',
    cancelName: 'Otkaži prijenos datoteke {name}',
    uploadedName: 'Datoteka {name} je prenesena',
    paused: 'Prijenos je pauziran, veza je prekinuta.',
    pausedName: '{name}: prijenos je pauziran, veza je prekinuta.',
    retry: 'Pokušajte ponovno',
    retryName: 'Ponovi prijenos datoteke {name}',
    remove: 'Ukloni',
    removeName: 'Ukloni datoteku {name}',
    unsupported:
      'Datoteke .{extension} ne mogu se prenijeti. Koristite PDF, Word, Excel, PowerPoint, CSV ili TSV.',
    unsupportedUnnamed:
      'Ova se datoteka ne može prenijeti. Koristite PDF, Word, Excel, PowerPoint, CSV ili TSV.',
    unsupportedOld:
      'Datoteke .{extension} ne mogu se prenijeti. Najprije je spremite kao .{modern}.',
    tooLarge: 'Ova datoteka ima {size}. Ograničenje je 500 MB.',
    empty: 'Ova datoteka je prazna.',
    notInFilter: 'Nije u ovom filtru',
    useInChat: 'Koristi u chatu',
    private: 'Privatno',
    storedOnly: 'Samo spremljeno · Chat još ne može čitati ovu datoteku',
    storedOnlyTooLarge: 'Samo spremljeno · Chat čita datoteke do 50 MiB',
  },
  cs: {
    addFiles: 'Přidat soubory',
    addFilesLine:
      'Přetáhněte je sem nebo je vyberte. PDF, Word, Excel, PowerPoint, CSV a TSV, každý do 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · do 500 MB',
    browse: 'Procházet',
    dropTitle: 'Uvolněním přidáte {files}',
    dropFiles: 'one:soubor|few:soubory|many:souboru|other:souborů',
    dropLine: 'Budou soukromé jen pro vás a připravené k použití v jakémkoli chatu.',
    waiting: 'Čeká na nahrání',
    progress: '{loaded} z {total}',
    timeLeft: 'Zbývající čas: asi {time}',
    finishing: 'Čtení souboru…',
    uploadingName: 'Nahrávání souboru {name}',
    cancelName: 'Zrušit nahrávání souboru {name}',
    uploadedName: 'Soubor {name} byl nahrán',
    paused: 'Nahrávání pozastaveno, připojení bylo přerušeno.',
    pausedName: '{name}: nahrávání pozastaveno, připojení bylo přerušeno.',
    retry: 'Zkusit znovu',
    retryName: 'Zkusit znovu nahrát {name}',
    remove: 'Odebrat',
    removeName: 'Odebrat {name}',
    unsupported:
      'Soubory .{extension} nelze nahrát. Použijte PDF, Word, Excel, PowerPoint, CSV nebo TSV.',
    unsupportedUnnamed:
      'Tento soubor nelze nahrát. Použijte PDF, Word, Excel, PowerPoint, CSV nebo TSV.',
    unsupportedOld: 'Soubory .{extension} nelze nahrát. Nejprve jej uložte jako .{modern}.',
    tooLarge: 'Tento soubor má {size}. Limit je 500 MB.',
    empty: 'Tento soubor je prázdný.',
    notInFilter: 'Není v tomto filtru',
    useInChat: 'Použít v chatu',
    private: 'Soukromé',
    storedOnly: 'Jen uloženo · Chat tento soubor zatím neumí číst',
    storedOnlyTooLarge: 'Jen uloženo · Chat čte soubory do 50 MiB',
  },
  da: {
    addFiles: 'Tilføj filer',
    addFilesLine:
      'Slip dem her, eller gennemse. PDF, Word, Excel, PowerPoint, CSV og TSV, op til 500 MB hver.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · op til 500 MB',
    browse: 'Gennemse',
    dropTitle: 'Slip for at tilføje {files}',
    dropFiles: 'one:fil|other:filer',
    dropLine: 'De bliver private for dig og klar til brug i enhver chat.',
    waiting: 'Venter på upload',
    progress: '{loaded} af {total}',
    timeLeft: 'Ca. {time} tilbage',
    finishing: 'Læser filen…',
    uploadingName: 'Uploader {name}',
    cancelName: 'Annuller upload af {name}',
    uploadedName: '{name} er uploadet',
    paused: 'Upload sat på pause, forbindelsen blev afbrudt.',
    pausedName: '{name}: upload sat på pause, forbindelsen blev afbrudt.',
    retry: 'Prøv igen',
    retryName: 'Prøv {name} igen',
    remove: 'Fjern',
    removeName: 'Fjern {name}',
    unsupported:
      'Kan ikke uploade .{extension}-filer. Brug PDF, Word, Excel, PowerPoint, CSV eller TSV.',
    unsupportedUnnamed:
      'Kan ikke uploade denne fil. Brug PDF, Word, Excel, PowerPoint, CSV eller TSV.',
    unsupportedOld: 'Kan ikke uploade .{extension}-filer. Gem den som .{modern} først.',
    tooLarge: 'Filen er {size}. Grænsen er 500 MB.',
    empty: 'Filen er tom.',
    notInFilter: 'Ikke i dette filter',
    useInChat: 'Brug i chat',
    private: 'Privat',
    storedOnly: 'Kun gemt · Chat kan ikke læse denne fil endnu',
    storedOnlyTooLarge: 'Kun gemt · Chat læser filer op til 50 MiB',
  },
  nl: {
    addFiles: 'Bestanden toevoegen',
    addFilesLine:
      'Sleep ze hierheen of blader. PDF, Word, Excel, PowerPoint, CSV en TSV, tot 500 MB per bestand.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · tot 500 MB',
    browse: 'Bladeren',
    dropTitle: 'Laat los om {files} toe te voegen',
    dropFiles: 'one:bestand|other:bestanden',
    dropLine: 'Ze blijven privé voor jou en zijn klaar voor gebruik in elke chat.',
    waiting: 'Wacht op uploaden',
    progress: '{loaded} van {total}',
    timeLeft: 'Nog ongeveer {time}',
    finishing: 'Bestand wordt gelezen…',
    uploadingName: '{name} wordt geüpload',
    cancelName: 'Uploaden van {name} annuleren',
    uploadedName: '{name} geüpload',
    paused: 'Uploaden gepauzeerd, verbinding verbroken.',
    pausedName: '{name}: uploaden gepauzeerd, verbinding verbroken.',
    retry: 'Opnieuw proberen',
    retryName: '{name} opnieuw proberen',
    remove: 'Verwijderen',
    removeName: '{name} verwijderen',
    unsupported:
      '.{extension}-bestanden kunnen niet worden geüpload. Gebruik PDF, Word, Excel, PowerPoint, CSV of TSV.',
    unsupportedUnnamed:
      'Dit bestand kan niet worden geüpload. Gebruik PDF, Word, Excel, PowerPoint, CSV of TSV.',
    unsupportedOld:
      '.{extension}-bestanden kunnen niet worden geüpload. Sla het eerst op als .{modern}.',
    tooLarge: 'Dit bestand is {size}. De limiet is 500 MB.',
    empty: 'Dit bestand is leeg.',
    notInFilter: 'Niet in dit filter',
    useInChat: 'Gebruiken in chat',
    private: 'Privé',
    storedOnly: 'Alleen opgeslagen · Chat kan dit bestand nog niet lezen',
    storedOnlyTooLarge: 'Alleen opgeslagen · Chat leest bestanden tot 50 MiB',
  },
  en,
  et: {
    addFiles: 'Lisa faile',
    addFilesLine:
      'Lohista need siia või sirvi. PDF, Word, Excel, PowerPoint, CSV ja TSV, igaüks kuni 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · kuni 500 MB',
    browse: 'Sirvi',
    dropTitle: 'Vabasta, et lisada {files}',
    dropFiles: 'one:fail|other:faili',
    dropLine: 'Need on privaatsed ainult sulle ja valmis kasutamiseks igas vestluses.',
    waiting: 'Ootab üleslaadimist',
    progress: '{loaded} / {total}',
    timeLeft: 'Jäänud umbes {time}',
    finishing: 'Faili lugemine…',
    uploadingName: '{name} laaditakse üles',
    cancelName: 'Tühista faili {name} üleslaadimine',
    uploadedName: '{name} on üles laaditud',
    paused: 'Üleslaadimine on peatatud, ühendus katkes.',
    pausedName: '{name}: üleslaadimine on peatatud, ühendus katkes.',
    retry: 'Proovi uuesti',
    retryName: 'Proovi faili {name} uuesti',
    remove: 'Eemalda',
    removeName: 'Eemalda {name}',
    unsupported:
      '.{extension}-faile ei saa üles laadida. Kasuta vorminguid PDF, Word, Excel, PowerPoint, CSV või TSV.',
    unsupportedUnnamed:
      'Seda faili ei saa üles laadida. Kasuta vorminguid PDF, Word, Excel, PowerPoint, CSV või TSV.',
    unsupportedOld:
      '.{extension}-faile ei saa üles laadida. Salvesta see enne vormingus .{modern}.',
    tooLarge: 'Selle faili suurus on {size}. Piirang on 500 MB.',
    empty: 'See fail on tühi.',
    notInFilter: 'Pole selles filtris',
    useInChat: 'Kasuta vestluses',
    private: 'Privaatne',
    storedOnly: 'Ainult salvestatud · Vestlus ei saa seda faili veel lugeda',
    storedOnlyTooLarge: 'Ainult salvestatud · Vestlus loeb faile kuni 50 MiB',
  },
  fi: {
    addFiles: 'Lisää tiedostoja',
    addFilesLine:
      'Pudota ne tähän tai selaa. PDF, Word, Excel, PowerPoint, CSV ja TSV, enintään 500 Mt kukin.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · enintään 500 Mt',
    browse: 'Selaa',
    dropTitle: 'Päästä irti lisätäksesi {files}',
    dropFiles: 'one:tiedoston|other:tiedostoa',
    dropLine: 'Ne näkyvät vain sinulle ja ovat valmiita käytettäväksi missä tahansa chatissa.',
    waiting: 'Odottaa latausta',
    progress: '{loaded} / {total}',
    timeLeft: 'Noin {time} jäljellä',
    finishing: 'Luetaan tiedostoa…',
    uploadingName: 'Ladataan tiedostoa {name}',
    cancelName: 'Peruuta tiedoston {name} lataus',
    uploadedName: 'Tiedosto {name} ladattu',
    paused: 'Lataus keskeytetty, yhteys katkesi.',
    pausedName: '{name}: lataus keskeytetty, yhteys katkesi.',
    retry: 'Yritä uudelleen',
    retryName: 'Lataa {name} uudelleen',
    remove: 'Poista',
    removeName: 'Poista {name}',
    unsupported:
      '.{extension}-tiedostoja ei voi ladata. Käytä muotoja PDF, Word, Excel, PowerPoint, CSV tai TSV.',
    unsupportedUnnamed:
      'Tätä tiedostoa ei voi ladata. Käytä muotoja PDF, Word, Excel, PowerPoint, CSV tai TSV.',
    unsupportedOld: '.{extension}-tiedostoja ei voi ladata. Tallenna se ensin muodossa .{modern}.',
    tooLarge: 'Tiedoston koko on {size}. Enimmäiskoko on 500 Mt.',
    empty: 'Tiedosto on tyhjä.',
    notInFilter: 'Ei tässä suodattimessa',
    useInChat: 'Käytä chatissa',
    private: 'Yksityinen',
    storedOnly: 'Vain tallennettu · Chat ei vielä voi lukea tätä tiedostoa',
    storedOnlyTooLarge: 'Vain tallennettu · Chat lukee enintään 50 MiB:n tiedostoja',
  },
  fr: {
    addFiles: 'Ajouter des fichiers',
    addFilesLine:
      'Déposez-les ici ou parcourez. PDF, Word, Excel, PowerPoint, CSV et TSV, jusqu’à 500 Mo chacun.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · jusqu’à 500 Mo',
    browse: 'Parcourir',
    dropTitle: 'Relâchez pour ajouter {files}',
    dropFiles: 'one:fichier|other:fichiers',
    dropLine: 'Ils ne seront visibles que par vous et prêts à servir dans n’importe quel chat.',
    waiting: 'En attente d’importation',
    progress: '{loaded} sur {total}',
    timeLeft: 'Encore environ {time}',
    finishing: 'Lecture du fichier…',
    uploadingName: 'Importation de {name}',
    cancelName: 'Annuler l’importation de {name}',
    uploadedName: '{name} importé',
    paused: 'Importation en pause, connexion perdue.',
    pausedName: '{name} : importation en pause, connexion perdue.',
    retry: 'Réessayer',
    retryName: 'Réessayer {name}',
    remove: 'Retirer',
    removeName: 'Retirer {name}',
    unsupported:
      'Impossible d’importer les fichiers .{extension}. Utilisez PDF, Word, Excel, PowerPoint, CSV ou TSV.',
    unsupportedUnnamed:
      'Impossible d’importer ce fichier. Utilisez PDF, Word, Excel, PowerPoint, CSV ou TSV.',
    unsupportedOld:
      'Impossible d’importer les fichiers .{extension}. Enregistrez-le d’abord au format .{modern}.',
    tooLarge: 'Ce fichier fait {size}. La limite est de 500 Mo.',
    empty: 'Ce fichier est vide.',
    notInFilter: 'Hors de ce filtre',
    useInChat: 'Utiliser dans le chat',
    private: 'Privé',
    storedOnly: 'Stocké uniquement · Le chat ne peut pas encore lire ce fichier',
    storedOnlyTooLarge: 'Stocké uniquement · Le chat lit les fichiers jusqu’à 50 Mio',
  },
  de: {
    addFiles: 'Dateien hinzufügen',
    addFilesLine:
      'Hier ablegen oder durchsuchen. PDF, Word, Excel, PowerPoint, CSV und TSV, jeweils bis 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · bis 500 MB',
    browse: 'Durchsuchen',
    dropTitle: 'Loslassen, um {files} hinzuzufügen',
    dropFiles: 'one:Datei|other:Dateien',
    dropLine: 'Die Dateien sind nur für Sie sichtbar und in jedem Chat nutzbar.',
    waiting: 'Wartet auf das Hochladen',
    progress: '{loaded} von {total}',
    timeLeft: 'Noch etwa {time}',
    finishing: 'Datei wird gelesen…',
    uploadingName: '{name} wird hochgeladen',
    cancelName: 'Hochladen von {name} abbrechen',
    uploadedName: '{name} hochgeladen',
    paused: 'Hochladen pausiert, Verbindung unterbrochen.',
    pausedName: '{name}: Hochladen pausiert, Verbindung unterbrochen.',
    retry: 'Erneut versuchen',
    retryName: '{name} erneut versuchen',
    remove: 'Entfernen',
    removeName: '{name} entfernen',
    unsupported:
      '.{extension}-Dateien können nicht hochgeladen werden. Verwenden Sie PDF, Word, Excel, PowerPoint, CSV oder TSV.',
    unsupportedUnnamed:
      'Diese Datei kann nicht hochgeladen werden. Verwenden Sie PDF, Word, Excel, PowerPoint, CSV oder TSV.',
    unsupportedOld:
      '.{extension}-Dateien können nicht hochgeladen werden. Speichern Sie sie zuerst als .{modern}.',
    tooLarge: 'Diese Datei ist {size} groß. Das Limit liegt bei 500 MB.',
    empty: 'Diese Datei ist leer.',
    notInFilter: 'Nicht in diesem Filter',
    useInChat: 'Im Chat verwenden',
    private: 'Privat',
    storedOnly: 'Nur gespeichert · Der Chat kann diese Datei noch nicht lesen',
    storedOnlyTooLarge: 'Nur gespeichert · Der Chat liest Dateien bis 50 MiB',
  },
  el: {
    addFiles: 'Προσθήκη αρχείων',
    addFilesLine:
      'Αποθέστε τα εδώ ή επιλέξτε τα. PDF, Word, Excel, PowerPoint, CSV και TSV, έως 500 MB το καθένα.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · έως 500 MB',
    browse: 'Περιήγηση',
    dropTitle: 'Αφήστε για να προσθέσετε {files}',
    dropFiles: 'one:αρχείο|other:αρχεία',
    dropLine: 'Θα είναι ιδιωτικά για εσάς και έτοιμα για χρήση σε οποιαδήποτε συνομιλία.',
    waiting: 'Αναμονή για μεταφόρτωση',
    progress: '{loaded} από {total}',
    timeLeft: 'Περίπου {time} ακόμη',
    finishing: 'Ανάγνωση του αρχείου…',
    uploadingName: 'Μεταφόρτωση του {name}',
    cancelName: 'Ακύρωση μεταφόρτωσης του {name}',
    uploadedName: 'Το {name} μεταφορτώθηκε',
    paused: 'Η μεταφόρτωση διακόπηκε προσωρινά, η σύνδεση χάθηκε.',
    pausedName: '{name}: η μεταφόρτωση διακόπηκε προσωρινά, η σύνδεση χάθηκε.',
    retry: 'Δοκιμάστε ξανά',
    retryName: 'Νέα προσπάθεια για το {name}',
    remove: 'Κατάργηση',
    removeName: 'Κατάργηση του {name}',
    unsupported:
      'Δεν είναι δυνατή η μεταφόρτωση αρχείων .{extension}. Χρησιμοποιήστε PDF, Word, Excel, PowerPoint, CSV ή TSV.',
    unsupportedUnnamed:
      'Δεν είναι δυνατή η μεταφόρτωση αυτού του αρχείου. Χρησιμοποιήστε PDF, Word, Excel, PowerPoint, CSV ή TSV.',
    unsupportedOld:
      'Δεν είναι δυνατή η μεταφόρτωση αρχείων .{extension}. Αποθηκεύστε το πρώτα ως .{modern}.',
    tooLarge: 'Αυτό το αρχείο είναι {size}. Το όριο είναι 500 MB.',
    empty: 'Αυτό το αρχείο είναι κενό.',
    notInFilter: 'Εκτός αυτού του φίλτρου',
    useInChat: 'Χρήση στη συνομιλία',
    private: 'Ιδιωτικό',
    storedOnly: 'Μόνο αποθηκευμένο · Η συνομιλία δεν μπορεί ακόμη να διαβάσει αυτό το αρχείο',
    storedOnlyTooLarge: 'Μόνο αποθηκευμένο · Η συνομιλία διαβάζει αρχεία έως 50 MiB',
  },
  hu: {
    addFiles: 'Fájlok hozzáadása',
    addFilesLine:
      'Húzza ide őket, vagy tallózzon. PDF, Word, Excel, PowerPoint, CSV és TSV, egyenként legfeljebb 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · legfeljebb 500 MB',
    browse: 'Tallózás',
    dropTitle: 'Engedje el {files} hozzáadásához',
    dropFiles: 'one:fájl|other:fájl',
    dropLine: 'Csak Ön fogja látni őket, és bármelyik csevegésben használhatja őket.',
    waiting: 'Feltöltésre vár',
    progress: '{loaded} / {total}',
    timeLeft: 'Még körülbelül {time}',
    finishing: 'A fájl olvasása…',
    uploadingName: '{name} feltöltése',
    cancelName: '{name} feltöltésének megszakítása',
    uploadedName: '{name} feltöltve',
    paused: 'A feltöltés szünetel, megszakadt a kapcsolat.',
    pausedName: '{name}: a feltöltés szünetel, megszakadt a kapcsolat.',
    retry: 'Próbálja újra',
    retryName: '{name} feltöltésének újrapróbálása',
    remove: 'Eltávolítás',
    removeName: '{name} eltávolítása',
    unsupported:
      '.{extension} fájlok nem tölthetők fel. Használjon PDF, Word, Excel, PowerPoint, CSV vagy TSV formátumot.',
    unsupportedUnnamed:
      'Ez a fájl nem tölthető fel. Használjon PDF, Word, Excel, PowerPoint, CSV vagy TSV formátumot.',
    unsupportedOld: '.{extension} fájlok nem tölthetők fel. Előbb mentse .{modern} formátumban.',
    tooLarge: 'A fájl mérete {size}. A korlát 500 MB.',
    empty: 'Ez a fájl üres.',
    notInFilter: 'Nem szerepel ebben a szűrőben',
    useInChat: 'Használat a csevegésben',
    private: 'Privát',
    storedOnly: 'Csak tárolva · A csevegés még nem tudja olvasni ezt a fájlt',
    storedOnlyTooLarge: 'Csak tárolva · A csevegés legfeljebb 50 MiB-os fájlokat olvas',
  },
  ga: {
    addFiles: 'Cuir comhaid leis',
    addFilesLine:
      'Scaoil anseo iad nó brabhsáil. PDF, Word, Excel, PowerPoint, CSV agus TSV, suas le 500 MB an ceann.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · suas le 500 MB',
    browse: 'Brabhsáil',
    dropTitle: 'Scaoil chun {files} a chur leis',
    dropFiles: 'one:chomhad|two:chomhad|few:chomhad|many:gcomhad|other:comhad',
    dropLine: 'Beidh siad príobháideach duit agus réidh le húsáid in aon chomhrá.',
    waiting: 'Ag fanacht le huaslódáil',
    progress: '{loaded} as {total}',
    timeLeft: 'Thart ar {time} fágtha',
    finishing: 'Ag léamh an chomhaid…',
    uploadingName: '{name} á uaslódáil',
    cancelName: 'Cealaigh uaslódáil {name}',
    uploadedName: '{name} uaslódáilte',
    paused: 'Uaslódáil curtha ar sos, cailleadh an ceangal.',
    pausedName: '{name}: uaslódáil curtha ar sos, cailleadh an ceangal.',
    retry: 'Bain triail eile as',
    retryName: 'Bain triail eile as {name}',
    remove: 'Bain',
    removeName: 'Bain {name}',
    unsupported:
      'Ní féidir comhaid .{extension} a uaslódáil. Úsáid PDF, Word, Excel, PowerPoint, CSV nó TSV.',
    unsupportedUnnamed:
      'Ní féidir an comhad seo a uaslódáil. Úsáid PDF, Word, Excel, PowerPoint, CSV nó TSV.',
    unsupportedOld: 'Ní féidir comhaid .{extension} a uaslódáil. Sábháil mar .{modern} é ar dtús.',
    tooLarge: 'Is é {size} méid an chomhaid seo. Is é 500 MB an teorainn.',
    empty: 'Tá an comhad seo folamh.',
    notInFilter: 'Ní sa scagaire seo',
    useInChat: 'Úsáid sa chomhrá',
    private: 'Príobháideach',
    storedOnly: 'Stóráilte amháin · Ní féidir leis an gcomhrá an comhad seo a léamh fós',
    storedOnlyTooLarge: 'Stóráilte amháin · Léann an comhrá comhaid suas le 50 MiB',
  },
  it: {
    addFiles: 'Aggiungi file',
    addFilesLine:
      'Trascinali qui o sfoglia. PDF, Word, Excel, PowerPoint, CSV e TSV, fino a 500 MB ciascuno.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · fino a 500 MB',
    browse: 'Sfoglia',
    dropTitle: 'Rilascia per aggiungere {files}',
    dropFiles: 'one:file|other:file',
    dropLine: 'Saranno privati per te e pronti da usare in qualsiasi chat.',
    waiting: 'In attesa di caricamento',
    progress: '{loaded} di {total}',
    timeLeft: 'Ancora circa {time}',
    finishing: 'Lettura del file…',
    uploadingName: 'Caricamento di {name}',
    cancelName: 'Annulla il caricamento di {name}',
    uploadedName: '{name} caricato',
    paused: 'Caricamento in pausa, connessione persa.',
    pausedName: '{name}: caricamento in pausa, connessione persa.',
    retry: 'Riprova',
    retryName: 'Riprova {name}',
    remove: 'Rimuovi',
    removeName: 'Rimuovi {name}',
    unsupported:
      'Impossibile caricare file .{extension}. Usa PDF, Word, Excel, PowerPoint, CSV o TSV.',
    unsupportedUnnamed:
      'Impossibile caricare questo file. Usa PDF, Word, Excel, PowerPoint, CSV o TSV.',
    unsupportedOld: 'Impossibile caricare file .{extension}. Salvalo prima come .{modern}.',
    tooLarge: 'Questo file è di {size}. Il limite è 500 MB.',
    empty: 'Questo file è vuoto.',
    notInFilter: 'Non in questo filtro',
    useInChat: 'Usa in chat',
    private: 'Privato',
    storedOnly: 'Solo archiviato · La chat non può ancora leggere questo file',
    storedOnlyTooLarge: 'Solo archiviato · La chat legge file fino a 50 MiB',
  },
  lv: {
    addFiles: 'Pievienot failus',
    addFilesLine:
      'Nometiet tos šeit vai pārlūkojiet. PDF, Word, Excel, PowerPoint, CSV un TSV, katrs līdz 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · līdz 500 MB',
    browse: 'Pārlūkot',
    dropTitle: 'Atlaidiet, lai pievienotu {files}',
    dropFiles: 'zero:failu|one:failu|other:failus',
    dropLine: 'Tie būs privāti tikai jums un gatavi lietošanai jebkurā tērzēšanā.',
    waiting: 'Gaida augšupielādi',
    progress: '{loaded} no {total}',
    timeLeft: 'Vēl aptuveni {time}',
    finishing: 'Lasa failu…',
    uploadingName: 'Augšupielādē failu {name}',
    cancelName: 'Atcelt faila {name} augšupielādi',
    uploadedName: 'Fails {name} augšupielādēts',
    paused: 'Augšupielāde apturēta, savienojums zaudēts.',
    pausedName: '{name}: augšupielāde apturēta, savienojums zaudēts.',
    retry: 'Mēģināt vēlreiz',
    retryName: 'Mēģināt vēlreiz augšupielādēt {name}',
    remove: 'Noņemt',
    removeName: 'Noņemt {name}',
    unsupported:
      'Nevar augšupielādēt .{extension} failus. Izmantojiet PDF, Word, Excel, PowerPoint, CSV vai TSV.',
    unsupportedUnnamed:
      'Šo failu nevar augšupielādēt. Izmantojiet PDF, Word, Excel, PowerPoint, CSV vai TSV.',
    unsupportedOld:
      'Nevar augšupielādēt .{extension} failus. Vispirms saglabājiet to kā .{modern}.',
    tooLarge: 'Šī faila lielums ir {size}. Ierobežojums ir 500 MB.',
    empty: 'Šis fails ir tukšs.',
    notInFilter: 'Nav šajā filtrā',
    useInChat: 'Izmantot tērzēšanā',
    private: 'Privāts',
    storedOnly: 'Tikai saglabāts · Tērzēšana šo failu vēl nevar nolasīt',
    storedOnlyTooLarge: 'Tikai saglabāts · Tērzēšana nolasa failus līdz 50 MiB',
  },
  lt: {
    addFiles: 'Pridėti failus',
    addFilesLine:
      'Nutempkite juos čia arba naršykite. PDF, Word, Excel, PowerPoint, CSV ir TSV, kiekvienas iki 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · iki 500 MB',
    browse: 'Naršyti',
    dropTitle: 'Atleiskite, kad pridėtumėte {files}',
    dropFiles: 'one:failą|few:failus|many:failo|other:failų',
    dropLine: 'Jie bus privatūs tik jums ir paruošti naudoti bet kuriame pokalbyje.',
    waiting: 'Laukiama įkėlimo',
    progress: '{loaded} iš {total}',
    timeLeft: 'Liko maždaug {time}',
    finishing: 'Skaitomas failas…',
    uploadingName: 'Įkeliamas failas {name}',
    cancelName: 'Atšaukti failo {name} įkėlimą',
    uploadedName: 'Failas {name} įkeltas',
    paused: 'Įkėlimas pristabdytas, prarastas ryšys.',
    pausedName: '{name}: įkėlimas pristabdytas, prarastas ryšys.',
    retry: 'Bandyti dar kartą',
    retryName: 'Bandyti dar kartą įkelti {name}',
    remove: 'Pašalinti',
    removeName: 'Pašalinti {name}',
    unsupported:
      'Negalima įkelti .{extension} failų. Naudokite PDF, Word, Excel, PowerPoint, CSV arba TSV.',
    unsupportedUnnamed:
      'Šio failo negalima įkelti. Naudokite PDF, Word, Excel, PowerPoint, CSV arba TSV.',
    unsupportedOld: 'Negalima įkelti .{extension} failų. Pirmiausia išsaugokite jį kaip .{modern}.',
    tooLarge: 'Šio failo dydis – {size}. Riba – 500 MB.',
    empty: 'Šis failas tuščias.',
    notInFilter: 'Nėra šiame filtre',
    useInChat: 'Naudoti pokalbyje',
    private: 'Privatus',
    storedOnly: 'Tik išsaugota · Pokalbis dar negali perskaityti šio failo',
    storedOnlyTooLarge: 'Tik išsaugota · Pokalbis skaito failus iki 50 MiB',
  },
  mt: {
    addFiles: 'Żid fajls',
    addFilesLine:
      'Itfagħhom hawn jew ibbrawżja. PDF, Word, Excel, PowerPoint, CSV u TSV, sa 500 MB kull wieħed.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · sa 500 MB',
    browse: 'Ibbrawżja',
    dropTitle: 'Erħi biex iżżid {files}',
    dropFiles: 'one:fajl|two:fajls|few:fajls|many:fajl|other:fajl',
    dropLine: 'Ikunu privati għalik u lesti biex tużahom f’kull chat.',
    waiting: 'Qed jistenna biex jittella’',
    progress: '{loaded} minn {total}',
    timeLeft: 'Fadal madwar {time}',
    finishing: 'Qed jinqara l-fajl…',
    uploadingName: 'Qed jittella’ {name}',
    cancelName: 'Ikkanċella t-tlugħ ta’ {name}',
    uploadedName: '{name} ittella’',
    paused: 'It-tlugħ twaqqaf, il-konnessjoni intilfet.',
    pausedName: '{name}: it-tlugħ twaqqaf, il-konnessjoni intilfet.',
    retry: 'Erġa’ pprova',
    retryName: 'Erġa’ pprova {name}',
    remove: 'Neħħi',
    removeName: 'Neħħi {name}',
    unsupported:
      'Ma tistax ittella’ fajls .{extension}. Uża PDF, Word, Excel, PowerPoint, CSV jew TSV.',
    unsupportedUnnamed:
      'Ma tistax ittella’ dan il-fajl. Uża PDF, Word, Excel, PowerPoint, CSV jew TSV.',
    unsupportedOld: 'Ma tistax ittella’ fajls .{extension}. Issejvjah l-ewwel bħala .{modern}.',
    tooLarge: 'Dan il-fajl huwa {size}. Il-limitu huwa 500 MB.',
    empty: 'Dan il-fajl huwa vojt.',
    notInFilter: 'Mhux f’dan il-filtru',
    useInChat: 'Uża fiċ-chat',
    private: 'Privat',
    storedOnly: 'Maħżun biss · Iċ-chat għadu ma jistax jaqra dan il-fajl',
    storedOnlyTooLarge: 'Maħżun biss · Iċ-chat jaqra fajls sa 50 MiB',
  },
  pl: {
    addFiles: 'Dodaj pliki',
    addFilesLine:
      'Upuść je tutaj lub przeglądaj. PDF, Word, Excel, PowerPoint, CSV i TSV, do 500 MB każdy.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · do 500 MB',
    browse: 'Przeglądaj',
    dropTitle: 'Upuść, aby dodać {files}',
    dropFiles: 'one:plik|few:pliki|many:plików|other:pliku',
    dropLine: 'Będą prywatne tylko dla Ciebie i gotowe do użycia w każdym czacie.',
    waiting: 'Oczekuje na przesłanie',
    progress: '{loaded} z {total}',
    timeLeft: 'Szacowany pozostały czas: {time}',
    finishing: 'Odczytywanie pliku…',
    uploadingName: 'Przesyłanie pliku {name}',
    cancelName: 'Anuluj przesyłanie pliku {name}',
    uploadedName: 'Przesłano plik {name}',
    paused: 'Przesyłanie wstrzymane, utracono połączenie.',
    pausedName: '{name}: przesyłanie wstrzymane, utracono połączenie.',
    retry: 'Spróbuj ponownie',
    retryName: 'Ponów przesyłanie pliku {name}',
    remove: 'Usuń',
    removeName: 'Usuń {name}',
    unsupported:
      'Nie można przesłać plików .{extension}. Użyj formatu PDF, Word, Excel, PowerPoint, CSV lub TSV.',
    unsupportedUnnamed:
      'Nie można przesłać tego pliku. Użyj formatu PDF, Word, Excel, PowerPoint, CSV lub TSV.',
    unsupportedOld: 'Nie można przesłać plików .{extension}. Najpierw zapisz go jako .{modern}.',
    tooLarge: 'Ten plik ma {size}. Limit to 500 MB.',
    empty: 'Ten plik jest pusty.',
    notInFilter: 'Poza tym filtrem',
    useInChat: 'Użyj na czacie',
    private: 'Prywatny',
    storedOnly: 'Tylko zapisano · Czat nie może jeszcze odczytać tego pliku',
    storedOnlyTooLarge: 'Tylko zapisano · Czat odczytuje pliki do 50 MiB',
  },
  pt: {
    addFiles: 'Adicionar ficheiros',
    addFilesLine:
      'Largue-os aqui ou procure. PDF, Word, Excel, PowerPoint, CSV e TSV, até 500 MB cada.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · até 500 MB',
    browse: 'Procurar',
    dropTitle: 'Largue para adicionar {files}',
    dropFiles: 'one:ficheiro|many:de ficheiros|other:ficheiros',
    dropLine: 'Ficarão privados para si e prontos a usar em qualquer chat.',
    waiting: 'A aguardar carregamento',
    progress: '{loaded} de {total}',
    timeLeft: 'Tempo restante: cerca de {time}',
    finishing: 'A ler o ficheiro…',
    uploadingName: 'A carregar {name}',
    cancelName: 'Cancelar o carregamento de {name}',
    uploadedName: '{name} carregado',
    paused: 'Carregamento em pausa, ligação perdida.',
    pausedName: '{name}: carregamento em pausa, ligação perdida.',
    retry: 'Tentar novamente',
    retryName: 'Tentar novamente {name}',
    remove: 'Remover',
    removeName: 'Remover {name}',
    unsupported:
      'Não é possível carregar ficheiros .{extension}. Use PDF, Word, Excel, PowerPoint, CSV ou TSV.',
    unsupportedUnnamed:
      'Não é possível carregar este ficheiro. Use PDF, Word, Excel, PowerPoint, CSV ou TSV.',
    unsupportedOld:
      'Não é possível carregar ficheiros .{extension}. Guarde-o primeiro como .{modern}.',
    tooLarge: 'Este ficheiro tem {size}. O limite é de 500 MB.',
    empty: 'Este ficheiro está vazio.',
    notInFilter: 'Fora deste filtro',
    useInChat: 'Usar no chat',
    private: 'Privado',
    storedOnly: 'Apenas armazenado · O chat ainda não consegue ler este ficheiro',
    storedOnlyTooLarge: 'Apenas armazenado · O chat lê ficheiros até 50 MiB',
  },
  ro: {
    addFiles: 'Adăugați fișiere',
    addFilesLine:
      'Plasați-le aici sau răsfoiți. PDF, Word, Excel, PowerPoint, CSV și TSV, de până la 500 MB fiecare.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · până la 500 MB',
    browse: 'Răsfoiți',
    dropTitle: 'Eliberați pentru a adăuga {files}',
    dropFiles: 'one:fișier|few:fișiere|other:de fișiere',
    dropLine: 'Vor fi private pentru dvs. și gata de folosit în orice chat.',
    waiting: 'Se așteaptă încărcarea',
    progress: '{loaded} din {total}',
    timeLeft: 'Timp rămas: aproximativ {time}',
    finishing: 'Se citește fișierul…',
    uploadingName: 'Se încarcă {name}',
    cancelName: 'Anulați încărcarea pentru {name}',
    uploadedName: '{name} a fost încărcat',
    paused: 'Încărcarea este întreruptă, conexiunea s-a pierdut.',
    pausedName: '{name}: încărcarea este întreruptă, conexiunea s-a pierdut.',
    retry: 'Încercați din nou',
    retryName: 'Încercați din nou {name}',
    remove: 'Eliminați',
    removeName: 'Eliminați {name}',
    unsupported:
      'Nu se pot încărca fișiere .{extension}. Folosiți PDF, Word, Excel, PowerPoint, CSV sau TSV.',
    unsupportedUnnamed:
      'Acest fișier nu poate fi încărcat. Folosiți PDF, Word, Excel, PowerPoint, CSV sau TSV.',
    unsupportedOld: 'Nu se pot încărca fișiere .{extension}. Salvați-l mai întâi ca .{modern}.',
    tooLarge: 'Acest fișier are {size}. Limita este de 500 MB.',
    empty: 'Acest fișier este gol.',
    notInFilter: 'Nu este în acest filtru',
    useInChat: 'Folosiți în chat',
    private: 'Privat',
    storedOnly: 'Doar stocat · Chatul nu poate citi încă acest fișier',
    storedOnlyTooLarge: 'Doar stocat · Chatul citește fișiere de până la 50 MiB',
  },
  sk: {
    addFiles: 'Pridať súbory',
    addFilesLine:
      'Presuňte ich sem alebo ich vyberte. PDF, Word, Excel, PowerPoint, CSV a TSV, každý do 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · do 500 MB',
    browse: 'Prehľadávať',
    dropTitle: 'Uvoľnením pridáte {files}',
    dropFiles: 'one:súbor|few:súbory|many:súboru|other:súborov',
    dropLine: 'Budú súkromné len pre vás a pripravené na použitie v akomkoľvek chate.',
    waiting: 'Čaká na nahranie',
    progress: '{loaded} z {total}',
    timeLeft: 'Zostávajúci čas: asi {time}',
    finishing: 'Číta sa súbor…',
    uploadingName: 'Nahráva sa súbor {name}',
    cancelName: 'Zrušiť nahrávanie súboru {name}',
    uploadedName: 'Súbor {name} bol nahraný',
    paused: 'Nahrávanie je pozastavené, pripojenie sa prerušilo.',
    pausedName: '{name}: nahrávanie je pozastavené, pripojenie sa prerušilo.',
    retry: 'Skúsiť znova',
    retryName: 'Skúsiť znova nahrať {name}',
    remove: 'Odstrániť',
    removeName: 'Odstrániť {name}',
    unsupported:
      'Súbory .{extension} nie je možné nahrať. Použite PDF, Word, Excel, PowerPoint, CSV alebo TSV.',
    unsupportedUnnamed:
      'Tento súbor nie je možné nahrať. Použite PDF, Word, Excel, PowerPoint, CSV alebo TSV.',
    unsupportedOld: 'Súbory .{extension} nie je možné nahrať. Najprv ho uložte ako .{modern}.',
    tooLarge: 'Tento súbor má {size}. Limit je 500 MB.',
    empty: 'Tento súbor je prázdny.',
    notInFilter: 'Nie je v tomto filtri',
    useInChat: 'Použiť v chate',
    private: 'Súkromné',
    storedOnly: 'Len uložené · Chat tento súbor zatiaľ nevie prečítať',
    storedOnlyTooLarge: 'Len uložené · Chat číta súbory do 50 MiB',
  },
  sl: {
    addFiles: 'Dodaj datoteke',
    addFilesLine:
      'Spustite jih sem ali prebrskajte. PDF, Word, Excel, PowerPoint, CSV in TSV, vsaka do 500 MB.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · do 500 MB',
    browse: 'Prebrskaj',
    dropTitle: 'Spustite, da dodate {files}',
    dropFiles: 'one:datoteko|two:datoteki|few:datoteke|other:datotek',
    dropLine: 'Zasebne bodo samo za vas in pripravljene za uporabo v katerem koli klepetu.',
    waiting: 'Čaka na nalaganje',
    progress: '{loaded} od {total}',
    timeLeft: 'Še približno {time}',
    finishing: 'Branje datoteke…',
    uploadingName: 'Nalaganje datoteke {name}',
    cancelName: 'Prekliči nalaganje datoteke {name}',
    uploadedName: 'Datoteka {name} je naložena',
    paused: 'Nalaganje je začasno ustavljeno, povezava je bila prekinjena.',
    pausedName: '{name}: nalaganje je začasno ustavljeno, povezava je bila prekinjena.',
    retry: 'Poskusi znova',
    retryName: 'Znova poskusi naložiti {name}',
    remove: 'Odstrani',
    removeName: 'Odstrani {name}',
    unsupported:
      'Datotek .{extension} ni mogoče naložiti. Uporabite PDF, Word, Excel, PowerPoint, CSV ali TSV.',
    unsupportedUnnamed:
      'Te datoteke ni mogoče naložiti. Uporabite PDF, Word, Excel, PowerPoint, CSV ali TSV.',
    unsupportedOld: 'Datotek .{extension} ni mogoče naložiti. Najprej jo shranite kot .{modern}.',
    tooLarge: 'Ta datoteka ima {size}. Omejitev je 500 MB.',
    empty: 'Ta datoteka je prazna.',
    notInFilter: 'Ni v tem filtru',
    useInChat: 'Uporabi v klepetu',
    private: 'Zasebno',
    storedOnly: 'Samo shranjeno · Klepet te datoteke še ne more prebrati',
    storedOnlyTooLarge: 'Samo shranjeno · Klepet bere datoteke do 50 MiB',
  },
  es: {
    addFiles: 'Añadir archivos',
    addFilesLine:
      'Suéltalos aquí o explora. PDF, Word, Excel, PowerPoint, CSV y TSV, hasta 500 MB cada uno.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · hasta 500 MB',
    browse: 'Explorar',
    dropTitle: 'Suelta para añadir {files}',
    dropFiles: 'one:archivo|other:archivos',
    dropLine: 'Serán privados para ti y estarán listos para usar en cualquier chat.',
    waiting: 'Esperando para subir',
    progress: '{loaded} de {total}',
    timeLeft: 'Tiempo restante: aprox. {time}',
    finishing: 'Leyendo el archivo…',
    uploadingName: 'Subiendo {name}',
    cancelName: 'Cancelar la subida de {name}',
    uploadedName: '{name} subido',
    paused: 'Subida en pausa, se perdió la conexión.',
    pausedName: '{name}: subida en pausa, se perdió la conexión.',
    retry: 'Reintentar',
    retryName: 'Reintentar {name}',
    remove: 'Quitar',
    removeName: 'Quitar {name}',
    unsupported:
      'No se pueden subir archivos .{extension}. Usa PDF, Word, Excel, PowerPoint, CSV o TSV.',
    unsupportedUnnamed:
      'No se puede subir este archivo. Usa PDF, Word, Excel, PowerPoint, CSV o TSV.',
    unsupportedOld: 'No se pueden subir archivos .{extension}. Guárdalo primero como .{modern}.',
    tooLarge: 'Este archivo ocupa {size}. El límite es de 500 MB.',
    empty: 'Este archivo está vacío.',
    notInFilter: 'Fuera de este filtro',
    useInChat: 'Usar en el chat',
    private: 'Privado',
    storedOnly: 'Solo almacenado · El chat aún no puede leer este archivo',
    storedOnlyTooLarge: 'Solo almacenado · El chat lee archivos de hasta 50 MiB',
  },
  sv: {
    addFiles: 'Lägg till filer',
    addFilesLine:
      'Släpp dem här eller bläddra. PDF, Word, Excel, PowerPoint, CSV och TSV, upp till 500 MB per fil.',
    addFilesLineShort: 'PDF, Office, CSV/TSV · upp till 500 MB',
    browse: 'Bläddra',
    dropTitle: 'Släpp för att lägga till {files}',
    dropFiles: 'one:fil|other:filer',
    dropLine: 'De blir privata för dig och redo att användas i alla chattar.',
    waiting: 'Väntar på att laddas upp',
    progress: '{loaded} av {total}',
    timeLeft: 'Ungefär {time} kvar',
    finishing: 'Läser filen…',
    uploadingName: 'Laddar upp {name}',
    cancelName: 'Avbryt uppladdningen av {name}',
    uploadedName: '{name} har laddats upp',
    paused: 'Uppladdningen har pausats, anslutningen bröts.',
    pausedName: '{name}: uppladdningen har pausats, anslutningen bröts.',
    retry: 'Försök igen',
    retryName: 'Försök igen med {name}',
    remove: 'Ta bort',
    removeName: 'Ta bort {name}',
    unsupported:
      'Det går inte att ladda upp .{extension}-filer. Använd PDF, Word, Excel, PowerPoint, CSV eller TSV.',
    unsupportedUnnamed:
      'Det går inte att ladda upp den här filen. Använd PDF, Word, Excel, PowerPoint, CSV eller TSV.',
    unsupportedOld: 'Det går inte att ladda upp .{extension}-filer. Spara den som .{modern} först.',
    tooLarge: 'Filen är {size}. Gränsen är 500 MB.',
    empty: 'Filen är tom.',
    notInFilter: 'Inte i det här filtret',
    useInChat: 'Använd i chatten',
    private: 'Privat',
    storedOnly: 'Endast lagrad · Chatten kan inte läsa den här filen ännu',
    storedOnlyTooLarge: 'Endast lagrad · Chatten läser filer upp till 50 MiB',
  },
};
export default libraryUploadCopy;
